import { useMemo } from 'react';
import { useIsSameUser } from '../../../../../hooks/proposal/use-state-checker-proposal';
import { useDispatch } from 'react-redux';
import { History, Request } from '../../../../../assets/icon/iconify';
import { useDynamicQuires } from '../../../../../hooks/services/dynamic-queries/use-dynamic-quires';
import { setAmendmentModal, setAmendmentModalOne } from '../../../../../features/actuarial/request-amendment/request-amendment.slice';
import { useProposalTypeData } from '../../../../../hooks/proposal/use-proposal-type-data';
import { useModalAccess } from '../../../../global/modal/use-modal-access';

export default function ProposalPreviewAmendments() {

    const dispatch = useDispatch();
    const { 
        isCustomize,
        isBookedProposal,
        isReleaseProposal,
        isAmendGMSProposal,
        isClosedProposal,
        isMarketingGMSDepartment,
        isActuarialDepartment,
    } = useProposalTypeData();
    const isSameUser = useIsSameUser();
    const { currentProposalId } = useModalAccess();

    const { getAmendmentHistoryLogs } = useDynamicQuires({
        proposalIdAmendment: currentProposalId,
    });
    
    const hasPendingLatestAmendment = useMemo(() => {
        if (!getAmendmentHistoryLogs?.length) return false;

        const latest = getAmendmentHistoryLogs[0];

        return latest?.amendment_status === 'PENDING';
    }, [getAmendmentHistoryLogs]);

    const isAmendmentHistory = isActuarialDepartment || isBookedProposal || isClosedProposal;

    const requestAmendmentTitle = isAmendmentHistory
        ? 'Amendment request history'
        : hasPendingLatestAmendment
            ? 'Amendment request is pending'
            : 'Request amendment from Actuarial?';

    const requestAmendmentSubTitle = isAmendmentHistory
        ? 'View all amendment requests.'
        : hasPendingLatestAmendment
            ? 'Amendment request is awaiting review by the Actuarial team.'
            : 'Submit an amendment request for revisions.';

    const isValidStatus = isReleaseProposal || isBookedProposal || isAmendGMSProposal || isClosedProposal;

    const canRequestAmendment = isValidStatus && isCustomize && isSameUser && isMarketingGMSDepartment;
    const canViewHistory = isValidStatus && isCustomize && isActuarialDepartment;
    const canView = canRequestAmendment || canViewHistory;

    const canViewRequestYes = canRequestAmendment && !hasPendingLatestAmendment && !isAmendGMSProposal && !isBookedProposal && !isClosedProposal;
    // const canViewRequestYes = true

    if (!canView) return null;

    const showHistoryButton = isAmendGMSProposal || isAmendmentHistory || isMarketingGMSDepartment;
    const hasAmendmentHistory = !!getAmendmentHistoryLogs?.length;

    return (
        <div className="rounded-md border border-gray-200 bg-white p-2 shadow-sm sm:p-3">
            <div className="flex items-center gap-1.5">
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded border border-[#186cb2]/10 text-[#186cb2]">
                    {isAmendmentHistory || isAmendGMSProposal ? <History color="#186cb2" size={15} /> : <Request color="#186cb2" size={15} />}
                </div>

                <div className="min-w-0 flex-1">
                    <p className="text-[11px] font-semibold text-[#186cb2]">
                        {isAmendGMSProposal ? 'Request Log History' : 'Amendment'}
                    </p>
                    <p className="truncate text-[10px] text-gray-500">
                        {isAmendGMSProposal ? 'Review all request logs for this proposal.' : requestAmendmentTitle}
                    </p>
                </div>

                {canViewRequestYes && (
                    <button type="button" onClick={() => dispatch(setAmendmentModalOne('isRequesting'))} className="flex h-6 shrink-0 items-center gap-1 rounded-md bg-[#186cb2] px-2 text-[9px] font-medium text-white transition hover:bg-[#125895] active:scale-95">
                        <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14" /><path d="M5 12h14" /></svg>
                        Request
                    </button>
                )}

                {showHistoryButton && (
                    <button type="button" onClick={() => dispatch(setAmendmentModal('showAmendmentHistory'))} className="flex h-6 shrink-0 items-center gap-1 rounded-md border border-[#186cb2]/30 bg-[#186cb2]/5 px-2 text-[9px] font-medium text-[#186cb2] transition hover:bg-[#186cb2]/10 active:scale-95">
                        <History color="#186cb2" size={12} />
                        <span className="hidden sm:inline">History</span>
                    </button>
                )}
            </div>

            {hasPendingLatestAmendment && !isAmendGMSProposal && (
                <div className="mt-1.5 flex items-center gap-1.5 rounded border border-amber-200 bg-amber-50 px-2 py-1">
                    <svg className="h-3 w-3 shrink-0 text-amber-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="9" />
                        <path d="M12 8v4" />
                        <path d="M12 16h.01" />
                    </svg>
                    <span className="text-[9px] font-medium text-amber-700">
                        Amendment request is currently pending review.
                    </span>
                </div>
            )}
        </div>
    );
}